import { useState, useEffect, useCallback, useMemo } from "react";
import api from "../../lib/api";
import { formatFechaHora } from "../../lib/dates";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";
import { Modal } from "../../components/ui/Modal";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { Table } from "../../components/ui/Table";

interface ModalidadPaquete {
  modalidadId: number;
  nombre: string;
  tipo: "diario" | "anual";
  precioVenta: number | null;
  precioBase: number;
}

interface PaqueteOption {
  id: number;
  eventoReferencia?: string | null;
  estado?: string;
  modalidades_paquete?: ModalidadPaquete[];
}

interface QrRow {
  id: number;
  token_preview: string;
  contratar_url: string;
  etiqueta: string | null;
  seguro_id: number;
  paquete_nombre: string | null;
  configuracion: string;
  activo: boolean;
  usos: number;
  creado_en: string;
  revocado_en: string | null;
}

function enlacePublicoContratar(contratarUrl: string): string {
  try {
    const u = new URL(contratarUrl);
    return `${window.location.origin}${u.pathname}${u.search}`;
  } catch {
    const m = contratarUrl.match(/(\/contratar\/[^/?#]+)/);
    return m ? `${window.location.origin}${m[1]}` : contratarUrl;
  }
}

function paqueteSelectLabel(p: PaqueteOption): string {
  const nombre = p.eventoReferencia?.trim() || "Sin nombre";
  const mods = p.modalidades_paquete ?? [];
  if (mods.length === 0) return nombre;
  const resumen = mods
    .map((m) => {
      const precio = m.precioVenta ?? m.precioBase;
      return `${m.nombre} (${m.tipo}) — ${Number(precio).toFixed(2)} €`;
    })
    .join(", ");
  return `${nombre} — ${resumen}`;
}

export function MisQRPage() {
  const [list, setList] = useState<QrRow[]>([]);
  const [paquetes, setPaquetes] = useState<PaqueteOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [seguroId, setSeguroId] = useState("");
  const [modalidadesPaqueteNuevo, setModalidadesPaqueteNuevo] = useState<ModalidadPaquete[]>([]);
  const [etiqueta, setEtiqueta] = useState("");
  const [creating, setCreating] = useState(false);
  const [editRow, setEditRow] = useState<QrRow | null>(null);
  const [editSeguroId, setEditSeguroId] = useState("");
  const [modalidadesPaqueteEdit, setModalidadesPaqueteEdit] = useState<ModalidadPaquete[]>([]);
  const [editEtiqueta, setEditEtiqueta] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ token: string; url: string } | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [viewQr, setViewQr] = useState<{ pageUrl: string; dataUrl: string } | null>(null);
  const [revocarId, setRevocarId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiedRowId, setCopiedRowId] = useState<number | null>(null);
  const [copiedModalLink, setCopiedModalLink] = useState(false);
  const [copiedCreatedLink, setCopiedCreatedLink] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<QrRow[]>("/cliente/qr");
      setList(Array.isArray(res.data) ? res.data : []);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const copyToClipboard = async (text: string, onDone: () => void) => {
    try {
      await navigator.clipboard.writeText(text);
      onDone();
    } catch {
      /* ignore */
    }
  };

  const copyRowUrl = (rowId: number, url: string) => {
    void copyToClipboard(url, () => {
      setCopiedRowId(rowId);
      setTimeout(() => {
        setCopiedRowId((cur) => (cur === rowId ? null : cur));
      }, 2000);
    });
  };

  const loadPaquetes = async (): Promise<PaqueteOption[]> => {
    try {
      const res = await api.get<
        Array<{
          id: number;
          estado?: string;
          origen?: string;
          eventoReferencia?: string | null;
          modalidades_paquete?: ModalidadPaquete[];
        }>
      >("/cliente/seguros", { params: { estado: "activo", limit: 100 } });
      const base = Array.isArray(res.data) ? res.data : [];
      const elegibles = base.filter(
        (s) =>
          s.estado === "activo" &&
          s.origen !== "qr" &&
          (s.modalidades_paquete?.length ?? 0) > 0
      );
      const details = await Promise.all(
        elegibles.map(async (s) => {
          try {
            const d = await api.get<PaqueteOption>(`/cliente/seguros/${s.id}`);
            return d.data;
          } catch {
            return { id: s.id, eventoReferencia: s.eventoReferencia, estado: s.estado };
          }
        })
      );
      setPaquetes(details);
      return details;
    } catch {
      setPaquetes([]);
      return [];
    }
  };

  const preseleccionarPrimerPaquete = async (details: PaqueteOption[]) => {
    if (details.length === 0) {
      setSeguroId("");
      setModalidadesPaqueteNuevo([]);
      return;
    }
    const primero = details[0]!;
    setSeguroId(String(primero.id));
    setModalidadesPaqueteNuevo(await loadModalidadesPaquete(primero.id));
  };

  const loadModalidadesPaquete = async (id: number): Promise<ModalidadPaquete[]> => {
    try {
      const res = await api.get<{ modalidades_paquete?: ModalidadPaquete[] }>(`/cliente/seguros/${id}`);
      return Array.isArray(res.data.modalidades_paquete) ? res.data.modalidades_paquete : [];
    } catch {
      return [];
    }
  };

  const openNuevo = async () => {
    setError(null);
    setCreated(null);
    setQrDataUrl(null);
    setSeguroId("");
    setModalidadesPaqueteNuevo([]);
    setEtiqueta("");
    const details = await loadPaquetes();
    await preseleccionarPrimerPaquete(details);
    setModalOpen(true);
  };

  const handleCrear = async () => {
    if (!seguroId) {
      setError("Selecciona un paquete");
      return;
    }
    const sid = parseInt(seguroId, 10);
    if (Number.isNaN(sid)) {
      setError("Paquete no válido");
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const res = await api.post<{ token: string; url: string }>("/cliente/qr", {
        seguro_id: sid,
        etiqueta: etiqueta.trim() || undefined,
      });
      setCreated(res.data);
      const pageUrl = `${window.location.origin}/contratar/${res.data.token}`;
      const QRCode = (await import("qrcode")).default;
      const dataUrl = await QRCode.toDataURL(pageUrl, {
        width: 300,
        margin: 2,
        color: { dark: "#1A3AB8", light: "#FFFFFF" },
      });
      setQrDataUrl(dataUrl);
      fetchList();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      setError(ax.response?.data?.error ?? "No se pudo crear el código QR");
    } finally {
      setCreating(false);
    }
  };

  const abrirVerQr = async (contratarUrl: string) => {
    const pageUrl = enlacePublicoContratar(contratarUrl);
    const QRCode = (await import("qrcode")).default;
    const dataUrl = await QRCode.toDataURL(pageUrl, {
      width: 300,
      margin: 2,
      color: { dark: "#1A3AB8", light: "#FFFFFF" },
    });
    setViewQr({ pageUrl, dataUrl });
  };

  const descargarQrPng = async (contratarUrl: string) => {
    const pageUrl = enlacePublicoContratar(contratarUrl);
    const QRCode = (await import("qrcode")).default;
    const dataUrl = await QRCode.toDataURL(pageUrl, {
      width: 300,
      margin: 2,
      color: { dark: "#1A3AB8", light: "#FFFFFF" },
    });
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = "qr-circuitcover.png";
    a.click();
  };

  const paqueteOptions = useMemo(
    () => paquetes.map((p) => ({ value: String(p.id), label: paqueteSelectLabel(p) })),
    [paquetes]
  );

  const openEditar = async (r: QrRow) => {
    await loadPaquetes();
    setEditRow(r);
    setEditSeguroId(String(r.seguro_id));
    setModalidadesPaqueteEdit(await loadModalidadesPaquete(r.seguro_id));
    setEditEtiqueta(r.etiqueta?.trim() ?? "");
    setEditError(null);
  };

  const handleGuardarEdicion = async () => {
    if (!editRow) return;
    if (!editSeguroId) {
      setEditError("Selecciona un paquete");
      return;
    }
    const sid = parseInt(editSeguroId, 10);
    if (Number.isNaN(sid)) {
      setEditError("Paquete no válido");
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      await api.patch(`/cliente/qr/${editRow.id}`, {
        etiqueta: editEtiqueta.trim(),
        seguro_id: sid,
      });
      setEditRow(null);
      await fetchList();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      setEditError(ax.response?.data?.error ?? "No se pudo guardar");
    } finally {
      setEditSaving(false);
    }
  };

  const handleRevocar = async () => {
    if (revocarId == null) return;
    try {
      await api.post(`/cliente/qr/${revocarId}/revocar`);
      await fetchList();
    } catch {
      /* ConfirmDialog se cierra igual */
    }
  };

  const activos = useMemo(() => list.filter((r) => r.activo), [list]);
  const revocados = useMemo(() => list.filter((r) => !r.activo), [list]);

  const baseColumns = [
    { key: "etiqueta", header: "Etiqueta", render: (r: QrRow) => r.etiqueta?.trim() || "—" },
    {
      key: "paquete",
      header: "Paquete",
      render: (r: QrRow) => r.paquete_nombre?.trim() || r.configuracion || "—",
    },
    { key: "usos", header: "Usos", render: (r: QrRow) => r.usos },
    {
      key: "estado",
      header: "Estado",
      render: (r: QrRow) => (r.activo ? "Activo" : "Revocado"),
    },
    {
      key: "creado",
      header: "Creado",
      render: (r: QrRow) => formatFechaHora(r.creado_en ?? null),
    },
    {
      key: "enlace",
      header: "Enlace web",
      render: (r: QrRow) => {
        const url = enlacePublicoContratar(r.contratar_url);
        return (
          <div className="flex flex-wrap items-center gap-2 max-w-[min(100%,28rem)]">
            <span className="text-xs text-gray-700 break-all" title={url}>
              {url}
            </span>
            <Button type="button" size="sm" variant="secondary" onClick={() => copyRowUrl(r.id, url)}>
              {copiedRowId === r.id ? "✓ Copiado" : "Copiar"}
            </Button>
          </div>
        );
      },
    },
  ];

  const columnsActivos = [
    ...baseColumns,
    {
      key: "acciones",
      header: "Acciones",
      render: (r: QrRow) => (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="ghost" onClick={() => void openEditar(r)}>
            Editar
          </Button>
          <Button size="sm" variant="secondary" onClick={() => void abrirVerQr(r.contratar_url)}>
            Ver QR
          </Button>
          <Button size="sm" variant="secondary" onClick={() => void descargarQrPng(r.contratar_url)}>
            Descargar PNG
          </Button>
          <Button size="sm" variant="danger" onClick={() => setRevocarId(r.id)}>
            Revocar
          </Button>
        </div>
      ),
    },
  ];

  const columnsRevocados = [
    ...baseColumns,
    {
      key: "revocado",
      header: "Revocado",
      render: (r: QrRow) => formatFechaHora(r.revocado_en ?? null),
    },
  ];

  const createdPageUrl = created ? `${window.location.origin}/contratar/${created.token}` : "";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-[#2C2C2C]">Mis códigos QR</h1>
        <Button onClick={() => void openNuevo()}>Nuevo QR</Button>
      </div>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-[#2C2C2C]">Códigos activos</h2>
        <Table<QrRow>
          columns={columnsActivos}
          data={activos}
          loading={loading}
          emptyMessage="No hay códigos QR activos. Crea uno para contratación pública."
        />
      </section>

      {!loading && revocados.length > 0 && (
        <section className="space-y-3 opacity-60">
          <h2 className="text-lg font-semibold text-gray-500">Códigos revocados</h2>
          <Table<QrRow>
            columns={columnsRevocados}
            data={revocados}
            emptyMessage="No hay códigos revocados."
          />
        </section>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setError(null);
        }}
        title="Nuevo código QR"
        footer={null}
      >
        <div className="space-y-4">
          {!created ? (
            <>
              <Select
                label="Paquete"
                options={paqueteOptions}
                value={seguroId}
                onChange={(e) => {
                  const val = e.target.value;
                  setSeguroId(val);
                  if (val) {
                    const id = parseInt(val, 10);
                    if (!Number.isNaN(id)) {
                      void loadModalidadesPaquete(id).then(setModalidadesPaqueteNuevo);
                    } else {
                      setModalidadesPaqueteNuevo([]);
                    }
                  } else {
                    setModalidadesPaqueteNuevo([]);
                  }
                }}
              />
              {seguroId && modalidadesPaqueteNuevo.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                  <p className="text-xs font-medium text-gray-500 mb-1">Modalidades del paquete</p>
                  <ul className="space-y-1">
                    {modalidadesPaqueteNuevo.map((m) => (
                      <li key={m.modalidadId}>
                        {m.nombre} ({m.tipo}) — {Number(m.precioVenta ?? m.precioBase).toFixed(2)} €
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <Input
                label="Etiqueta (opcional)"
                value={etiqueta}
                onChange={(e) => setEtiqueta(e.target.value)}
                placeholder="Ej. Recepción circuito norte"
              />
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setModalOpen(false)}>
                  Cancelar
                </Button>
                <Button loading={creating} onClick={() => void handleCrear()}>
                  Crear
                </Button>
              </div>
            </>
          ) : (
            <div className="space-y-4 text-center">
              <div className="text-left space-y-2">
                <label className="block text-xs font-medium text-gray-600">Enlace web</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    readOnly
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 bg-gray-50"
                    value={createdPageUrl}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    className="shrink-0"
                    onClick={() =>
                      void copyToClipboard(createdPageUrl, () => {
                        setCopiedCreatedLink(true);
                        setTimeout(() => setCopiedCreatedLink(false), 2000);
                      })
                    }
                  >
                    {copiedCreatedLink ? "✓ Copiado" : "Copiar enlace"}
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Puedes usar este enlace en tu web o redes sociales para que los pilotos contraten directamente
                </p>
              </div>
              {qrDataUrl && <img src={qrDataUrl} alt="Código QR" className="mx-auto rounded-lg border" />}
              <a
                href={qrDataUrl ?? "#"}
                download="qr-circuitcover.png"
                className="inline-block rounded-lg bg-[#1A3AB8] px-4 py-2 text-sm font-medium text-white hover:bg-[#15308a]"
              >
                Descargar QR
              </a>
              <div>
                <Button
                  variant="secondary"
                  onClick={() => {
                    void (async () => {
                      setCreated(null);
                      setQrDataUrl(null);
                      setCopiedCreatedLink(false);
                      setEtiqueta("");
                      const details = await loadPaquetes();
                      await preseleccionarPrimerPaquete(details);
                    })();
                  }}
                >
                  Crear otro
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={!!viewQr}
        onClose={() => {
          setViewQr(null);
          setCopiedModalLink(false);
        }}
        title="Código QR"
        footer={null}
      >
        {viewQr && (
          <div className="space-y-4">
            <img src={viewQr.dataUrl} alt="QR" className="mx-auto rounded-lg border block" />
            <div className="space-y-2">
              <label className="block text-xs font-medium text-gray-600">Enlace web</label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  readOnly
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 bg-gray-50"
                  value={viewQr.pageUrl}
                />
                <Button
                  type="button"
                  variant="secondary"
                  className="shrink-0"
                  onClick={() =>
                    void copyToClipboard(viewQr.pageUrl, () => {
                      setCopiedModalLink(true);
                      setTimeout(() => setCopiedModalLink(false), 2000);
                    })
                  }
                >
                  {copiedModalLink ? "✓ Copiado" : "Copiar enlace"}
                </Button>
              </div>
            </div>
            <div className="text-center">
              <a
                href={viewQr.dataUrl}
                download="qr-circuitcover.png"
                className="inline-block rounded-lg bg-[#1A3AB8] px-4 py-2 text-sm font-medium text-white hover:bg-[#15308a]"
              >
                Descargar QR
              </a>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={editRow != null}
        onClose={() => {
          setEditRow(null);
          setEditError(null);
        }}
        title="Editar código QR"
        footer={null}
      >
        {editRow && (
          <div className="space-y-4">
            <Select
              label="Paquete"
              options={paqueteOptions}
              value={editSeguroId}
              onChange={(e) => {
                const val = e.target.value;
                setEditSeguroId(val);
                if (val) {
                  const id = parseInt(val, 10);
                  if (!Number.isNaN(id)) {
                    void loadModalidadesPaquete(id).then(setModalidadesPaqueteEdit);
                  } else {
                    setModalidadesPaqueteEdit([]);
                  }
                } else {
                  setModalidadesPaqueteEdit([]);
                }
              }}
            />
            {editSeguroId && modalidadesPaqueteEdit.length > 0 && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                <p className="text-xs font-medium text-gray-500 mb-1">Modalidades del paquete</p>
                <ul className="space-y-1">
                  {modalidadesPaqueteEdit.map((m) => (
                    <li key={m.modalidadId}>
                      {m.nombre} ({m.tipo}) — {Number(m.precioVenta ?? m.precioBase).toFixed(2)} €
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <Input
              label="Etiqueta"
              value={editEtiqueta}
              onChange={(e) => setEditEtiqueta(e.target.value)}
              placeholder="Etiqueta opcional"
            />
            {editError && <p className="text-sm text-red-600">{editError}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setEditRow(null)}>
                Cancelar
              </Button>
              <Button loading={editSaving} onClick={() => void handleGuardarEdicion()}>
                Guardar
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={revocarId != null}
        onClose={() => setRevocarId(null)}
        onConfirm={() => handleRevocar()}
        title="Revocar código QR"
        message="El enlace dejará de funcionar para nuevas contrataciones. ¿Continuar?"
        confirmLabel="Revocar"
        variant="danger"
      />
    </div>
  );
}
